import React, { useState, useEffect, useRef, ReactElement } from "react";
import { useTranslation } from "react-i18next";

import StyledLoading from "../../atoms/Loading/StyledLoading";
import WineDetailsRecipeCard from "../../atoms/WineDetailsRecipeCard";
import externalUrls from "../../../helpers/externalUrls";
import { I18N_NAMESPACES } from "../../../helpers/constants";

import { executeBlogGhostApi } from "../../../helpers/ghost";
import { GhostPostType } from "../../../types/ghost";
import { GetWine_wine_recipes as WineRecipes } from "../../../types/__generated__/GetWine";
import ArrowNavigator from "../../atoms/ArrowNavigator";
import { logError } from "../../../helpers/logging";
import { BlogPostsWrapper, ArrowWrapper, SeeAllTextLink } from "./style";

interface RecipeBlogPostsProps {
  recipes: WineRecipes[] | undefined;
  isWineDetailsPage: boolean;
}

const RecipeBlogPosts = ({ recipes, isWineDetailsPage }: RecipeBlogPostsProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);

  const [isLoadingBlogPosts, setIsLoadingBlogPosts] = useState<boolean>(false);
  const [blogPosts, setBlogPosts] = useState<(GhostPostType | null)[]>([]);

  const hasMoreThanOneRecipe = (recipes?.length ?? 0) > 1;
  const blogUrl = `${externalUrls.BLOG}/`;

  const horizontalContainer = useRef<HTMLDivElement | null>(null);

  const recipeSlugs =
    recipes?.map((recipe) => {
      const slug = recipe?.url?.replace(blogUrl, "");
      return slug.replace("/", ""); // remove trailing '/'
    }) ?? [];

  useEffect(() => {
    const fetchBlogPosts = async () => {
      setIsLoadingBlogPosts(true);
      try {
        const fetchedBlogPosts = await executeBlogGhostApi(
          `slug:[${Array.from(recipeSlugs).join(",")}]`,
          recipeSlugs?.length ?? 0
        );
        setBlogPosts(fetchedBlogPosts);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        logError(error, { location: "RecipeBlogPosts", action: "FETCH_RECIPE_BLOG_POSTS" });
      }
      setIsLoadingBlogPosts(false);
    };

    // Make sure that the recipes are in the right format before calling fetchBlogPosts.
    let shouldFetchRecipes = true;
    recipes?.forEach((recipe) => {
      if (!recipe?.url?.includes(blogUrl)) {
        shouldFetchRecipes = false;
      }
    });
    shouldFetchRecipes && fetchBlogPosts();
  }, []);

  const blogPostsLength = blogPosts?.length ?? 0;

  const renderContent = (): ReactElement => (
    <>
      {hasMoreThanOneRecipe && (
        <ArrowWrapper>
          <SeeAllTextLink href={externalUrls.BLOG} target="_blank" isWineDetailsPage={isWineDetailsPage}>
            {t("common:seeAll")}
          </SeeAllTextLink>
          <ArrowNavigator horizontalContainer={horizontalContainer} scrollAdjustment={0} />
        </ArrowWrapper>
      )}
      <BlogPostsWrapper ref={horizontalContainer}>
        {isLoadingBlogPosts ? (
          <StyledLoading />
        ) : (
          blogPosts?.map(
            (post: GhostPostType | null, index: number) =>
              post && (
                <WineDetailsRecipeCard
                  key={post?.id}
                  blogPost={post}
                  isLastBlogPost={blogPostsLength - 1 === index}
                />
              )
          )
        )}
      </BlogPostsWrapper>
    </>
  );

  if (!blogPosts) return null;
  return isLoadingBlogPosts ? <StyledLoading /> : renderContent();
};

export default RecipeBlogPosts;
